import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { Lock, ShoppingBag, TriangleAlert, FlaskConical } from "lucide-react";
import ReconAddons from "@/components/ReconAddons";

const DISCLAIMER =
    "Grayhaven Research materials are sold strictly for legitimate laboratory research purposes and are not intended for human or veterinary use. We do not provide dosing, administration, reconstitution, or usage guidance, and our team cannot answer questions regarding use in humans or animals.";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const priceNumber = (raw) => {
    const n = parseFloat(String(raw || "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
};

const Field = ({ label, ...props }) => (
    <label className="block">
        <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">{label}</span>
        <input
            {...props}
            className="mt-2 w-full border border-stone-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-wine/50 focus:outline-none transition-colors duration-300"
        />
    </label>
);

export default function Checkout() {
    const { user, openAuth } = useAuth();
    const { items, clear } = useCart();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [busy, setBusy] = useState(false);
    const [config, setConfig] = useState(null);
    const [method, setMethod] = useState("");
    const [discount, setDiscount] = useState("");
    const [appliedDiscount, setAppliedDiscount] = useState(null);
    const [quote, setQuote] = useState(null);
    const [form, setForm] = useState({
        name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "United States",
    });

    useEffect(() => {
        if (params.get("cancelled") || params.get("canceled")) {
            toast("Payment not completed", {
                description: "No charge was made. Your cart is unchanged — choose a payment method to try again.",
            });
        }
    }, [params]);

    useEffect(() => {
        api.get("/checkout/config").then((res) => {
            setConfig(res.data);
            const first = (res.data.payment_methods || [])[0];
            if (first) setMethod(first.key);
        }).catch(() => setConfig({ payment_methods: [] }));
    }, []);

    useEffect(() => {
        if (user && !form.name) {
            setForm((f) => ({ ...f, name: [user.first_name, user.last_name].filter(Boolean).join(" ") }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const cartItems = useMemo(
        () => items.map((i) => ({ id: i.id, variant_label: i.variant_label, qty: i.qty || 1 })),
        [items]
    );

    // Server-authoritative quote, refreshed whenever items/method/discount change
    useEffect(() => {
        if (!user || !user.email_verified || !cartItems.length) return;
        api.post("/checkout/quote", {
            items: cartItems,
            discount_code: appliedDiscount,
            payment_method: method || null,
        })
            .then((res) => setQuote(res.data))
            .catch(() => setQuote(null));
    }, [user, cartItems, method, appliedDiscount]);

    const subtotal = useMemo(
        () => items.reduce((sum, i) => sum + priceNumber(i.price) * (i.qty || 1), 0),
        [items]
    );

    const applyDiscount = async () => {
        if (!discount.trim()) return;
        try {
            const res = await api.post("/checkout/quote", {
                items: cartItems,
                discount_code: discount.trim(),
                payment_method: method || null,
            });
            setAppliedDiscount(discount.trim().toUpperCase());
            setQuote(res.data);
            toast.success("Discount applied", { description: discount.trim().toUpperCase() });
        } catch (err) {
            setAppliedDiscount(null);
            toast.error("Discount code not recognized", {
                description: err.response?.data?.detail || "Check the code and try again.",
            });
        }
    };

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const input = (key, extra = {}) => ({ value: form[key], onChange: set(key), required: true, ...extra });

    const submit = async (e) => {
        e.preventDefault();
        if (busy || !method) return;
        setBusy(true);
        try {
            const res = await api.post("/orders", {
                name: form.name,
                shipping: {
                    line1: form.line1, line2: form.line2, city: form.city,
                    state: form.state, postal_code: form.postal_code, country: form.country,
                },
                items: cartItems,
                discount_code: appliedDiscount,
                payment_method: method,
            });
            if (method === "stripe") {
                const s = await api.post(`/orders/${res.data.order_id}/stripe-checkout`, {
                    origin_url: window.location.origin,
                });
                window.location.href = s.data.checkout_url;
                return; // cart clears on the success page once payment confirms
            }
            clear();
            navigate(`/checkout/success?order=${res.data.order_id}`);
        } catch (err) {
            toast.error("Could not place order", {
                description: err.response?.data?.detail || "Please try again.",
            });
            setBusy(false);
        }
    };

    const selectedMethod = (config?.payment_methods || []).find((m) => m.key === method);

    let body;
    if (user === null) {
        body = null;
    } else if (!user) {
        body = (
            <Reveal>
                <div className="max-w-xl mx-auto border border-dashed border-gold/30 rounded-sm py-16 px-8 text-center bg-white">
                    <Lock className="h-9 w-9 text-gold/50 mx-auto" strokeWidth={1.25} />
                    <h2 className="mt-6 font-serif text-2xl font-light text-brand">Account required</h2>
                    <p className="mt-4 text-sm text-mist leading-relaxed">
                        Create a free account and verify your email to check out.
                    </p>
                    <div className="mt-8">
                        <ButtonPrimary onClick={() => openAuth("register")} data-testid="checkout-register-btn">
                            Create Account
                        </ButtonPrimary>
                    </div>
                </div>
            </Reveal>
        );
    } else if (!user.email_verified) {
        body = (
            <Reveal>
                <div className="max-w-xl mx-auto border border-dashed border-gold/30 rounded-sm py-16 px-8 text-center bg-white">
                    <Lock className="h-9 w-9 text-gold/50 mx-auto" strokeWidth={1.25} />
                    <h2 className="mt-6 font-serif text-2xl font-light text-brand">Verify your email</h2>
                    <p className="mt-4 text-sm text-mist leading-relaxed">
                        A verified email address is required before ordering. Check your inbox for the
                        verification link.
                    </p>
                    <div className="mt-8">
                        <ButtonPrimary onClick={() => openAuth("verify")} data-testid="checkout-verify-btn">
                            Verification Options
                        </ButtonPrimary>
                    </div>
                </div>
            </Reveal>
        );
    } else if (items.length === 0) {
        body = (
            <Reveal>
                <div className="max-w-xl mx-auto border border-dashed border-gold/30 rounded-sm py-16 px-8 text-center bg-white">
                    <ShoppingBag className="h-9 w-9 text-gold/50 mx-auto" strokeWidth={1.25} />
                    <h2 className="mt-6 font-serif text-2xl font-light text-brand">Your cart is empty</h2>
                    <div className="mt-8">
                        <ButtonSecondary to="/materials" data-testid="checkout-browse-btn">
                            Browse Research Materials
                        </ButtonSecondary>
                    </div>
                </div>
            </Reveal>
        );
    } else {
        body = (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 max-w-6xl mx-auto">
                <Reveal className="lg:col-span-3">
                    <form onSubmit={submit} className="bg-white border border-stone-200 p-7 sm:p-10" data-testid="checkout-form">
                        <p className="eyebrow">Contact</p>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Full name" data-testid="checkout-name" autoComplete="name" {...input("name")} />
                            <label className="block">
                                <span className="text-[0.6rem] uppercase tracking-[0.22em] text-slate-500 font-semibold">Email</span>
                                <input value={user.email} readOnly data-testid="checkout-email"
                                    className="mt-2 w-full border border-stone-200 bg-surface2 px-4 py-3 text-sm text-slate-500" />
                            </label>
                        </div>

                        <p className="eyebrow mt-10">Shipping Address</p>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="sm:col-span-2">
                                <Field label="Address line 1" data-testid="checkout-address1" autoComplete="address-line1" {...input("line1")} />
                            </div>
                            <div className="sm:col-span-2">
                                <Field label="Address line 2 (optional)" data-testid="checkout-address2" required={false} value={form.line2} onChange={set("line2")} />
                            </div>
                            <Field label="City" data-testid="checkout-city" autoComplete="address-level2" {...input("city")} />
                            <Field label="State / Region" data-testid="checkout-state" required={false} value={form.state} onChange={set("state")} />
                            <Field label="Postal code" data-testid="checkout-postal" autoComplete="postal-code" {...input("postal_code")} />
                            <Field label="Country" data-testid="checkout-country" autoComplete="country-name" {...input("country")} />
                        </div>

                        <p className="eyebrow mt-10">Payment Method</p>
                        <div className="mt-6 space-y-4" data-testid="checkout-payment-methods">
                            {(config?.payment_methods || []).map((m) => (
                                <label
                                    key={m.key}
                                    data-testid={`checkout-method-${m.key}`}
                                    className={`block cursor-pointer border p-5 transition-colors duration-300 ${
                                        method === m.key ? "border-wine bg-wine/5" : "border-stone-200 bg-white hover:border-wine/40"
                                    }`}
                                >
                                    <span className="flex items-center gap-3">
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            checked={method === m.key}
                                            onChange={() => setMethod(m.key)}
                                            className="h-4 w-4 accent-[#7A2230]"
                                        />
                                        <span className="text-sm font-semibold text-brand">{m.label}</span>
                                        {m.is_payment_link && (
                                            <span className="text-[0.6rem] uppercase tracking-[0.18em] text-wine font-semibold">
                                                {config?.payment_link_fee_percent ?? 5}% fee applies
                                            </span>
                                        )}
                                    </span>
                                    {m.instructions && (
                                        <span className="mt-2 block text-xs leading-relaxed text-mist pl-7">{m.instructions}</span>
                                    )}
                                </label>
                            ))}
                        </div>

                        <div className="mt-8 flex items-start gap-3 bg-surface2 border border-gold/25 p-5" data-testid="checkout-disclaimer">
                            <TriangleAlert className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                            <p className="text-xs leading-relaxed text-slate-600">{DISCLAIMER}</p>
                        </div>

                        <div className="mt-8">
                            <ButtonPrimary type="submit" disabled={busy || !method} className="w-full justify-center" data-testid="checkout-place-order-btn">
                                {busy ? "Placing order…" : `Place Order${quote ? ` — ${money(quote.total_cents)}` : ""}`}
                            </ButtonPrimary>
                        </div>
                    </form>
                </Reveal>

                <Reveal delay={0.1} className="lg:col-span-2">
                    <div className="bg-[#FDFAF5] border border-taupe/50 p-7 sm:p-8" data-testid="checkout-summary">
                        <p className="eyebrow">Order Summary</p>
                        <div className="mt-6 divide-y divide-stone-200/80">
                            {items.map((item) => (
                                <div key={item.key} className="py-4 flex items-center gap-4" data-testid={`checkout-item-${item.key.replace(/[^a-z0-9]+/gi, "-")}`}>
                                    <img
                                        src={`/assets/products/${item.id}.jpg`}
                                        alt={item.name}
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                            const fallback = e.currentTarget.nextElementSibling;
                                            if (fallback) fallback.style.display = "flex";
                                        }}
                                        className="h-16 w-12 object-cover border border-stone-200 shrink-0"
                                    />
                                    <span style={{ display: "none" }} className="h-16 w-12 bg-surface2 border border-stone-200 shrink-0 items-center justify-center">
                                        <FlaskConical className="h-5 w-5 text-gold/50" strokeWidth={1.25} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-base text-brand truncate">{item.name}</p>
                                        <p className="mt-0.5 text-xs text-mist">
                                            {item.variant_label} · {money(priceNumber(item.price) * 100)} × {item.qty || 1}
                                        </p>
                                    </div>
                                    <span className="text-sm text-slate-700 shrink-0">
                                        {money(priceNumber(item.price) * (item.qty || 1) * 100)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <ReconAddons className="mt-7 border-t border-taupe/60 pt-6" />

                        <div className="mt-6 flex gap-3">
                            <input
                                value={discount}
                                onChange={(e) => setDiscount(e.target.value)}
                                placeholder="Discount code"
                                data-testid="checkout-discount-input"
                                className="flex-1 border border-stone-200 bg-white px-4 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-wine/50 focus:outline-none"
                            />
                            <ButtonSecondary type="button" onClick={applyDiscount} className="px-5 py-2.5" data-testid="checkout-discount-apply-btn">
                                Apply
                            </ButtonSecondary>
                        </div>

                        <dl className="mt-6 pt-5 border-t border-taupe/60 space-y-2.5 text-sm">
                            <div className="flex justify-between">
                                <dt className="text-slate-500">Subtotal</dt>
                                <dd className="text-slate-700" data-testid="checkout-subtotal">{money(quote ? quote.subtotal_cents : subtotal * 100)}</dd>
                            </div>
                            {quote?.discount_cents > 0 && (
                                <div className="flex justify-between">
                                    <dt className="text-slate-500">Discount ({quote.discount_code})</dt>
                                    <dd className="text-wine" data-testid="checkout-discount-line">−{money(quote.discount_cents)}</dd>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <dt className="text-slate-500">Shipping</dt>
                                <dd className="text-slate-700" data-testid="checkout-shipping">
                                    {quote
                                        ? quote.free_shipping_applied
                                            ? "Free"
                                            : quote.shipping_cents
                                                ? money(quote.shipping_cents)
                                                : "Arranged after order"
                                        : "—"}
                                </dd>
                            </div>
                            <div className="flex justify-between">
                                <dt className="text-slate-500">Tax</dt>
                                <dd className="text-slate-700" data-testid="checkout-tax">
                                    {quote ? (quote.tax_cents ? money(quote.tax_cents) : "None") : "—"}
                                </dd>
                            </div>
                            {selectedMethod?.is_payment_link && (
                                <div className="flex justify-between">
                                    <dt className="text-slate-500">
                                        Payment-link fee ({quote?.payment_link_fee_percent ?? config?.payment_link_fee_percent ?? 5}%)
                                    </dt>
                                    <dd className="text-slate-700" data-testid="checkout-fee">
                                        {quote ? money(quote.payment_link_fee_cents) : "—"}
                                    </dd>
                                </div>
                            )}
                            <div className="flex justify-between pt-3 border-t border-taupe/60">
                                <dt className="text-[0.65rem] uppercase tracking-[0.25em] text-slate-500 font-semibold">Total</dt>
                                <dd className="font-serif text-2xl text-wine" data-testid="checkout-total">
                                    {quote ? money(quote.total_cents) : money(subtotal * 100)}
                                </dd>
                            </div>
                        </dl>
                        <p className="mt-5 text-[0.65rem] leading-relaxed text-slate-400">
                            {selectedMethod?.is_payment_link
                                ? "You place the order now; we review it and email your payment link. Payment happens separately — placing an order never marks it paid."
                                : selectedMethod?.instructions || "You place the order now; payment follows separately."}
                        </p>
                    </div>
                </Reveal>

                {/* All-sales-final notice: full-width, bottom of the page after the summary */}
                <Reveal delay={0.15} className="lg:col-span-5">
                    <div className="bg-[#FBF3E4] border border-gold/40 p-5" data-testid="checkout-final-sale-disclaimer">
                        <p className="text-sm font-semibold text-[#8A6D2F]">All sales are final.</p>
                        <p className="mt-2 text-xs leading-relaxed text-[#7A6538]">
                            Due to the nature of our products and to ensure product integrity, we do not
                            accept returns or provide refunds on any purchases. Please carefully review your
                            order before completing your purchase.
                        </p>
                        <p className="mt-3 text-xs leading-relaxed text-[#7A6538]">
                            In the event of a shipping error or damaged product, please contact us within 24
                            hours of delivery with photographic evidence. We will review each case
                            individually.
                        </p>
                    </div>
                </Reveal>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page">
            <PageHeader
                eyebrow="Secure Checkout"
                title="Checkout"
                description="Review your order, choose a payment method, and place your order. Payment is always a separate step."
            />
            <section className="pb-24 sm:pb-32 px-6 lg:px-8">{body}</section>
        </div>
    );
}
